import { createNetworkConfig, SuiClientProvider, WalletProvider } from '@mysten/dapp-kit';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { HelmetProvider } from 'react-helmet-async';
import { getFullnodeUrl } from '@mysten/sui/client';
import '@/css/global.css';
import '@mysten/dapp-kit/dist/index.css';
import { APP_CFG } from './configs';
import { createAppQueryClient } from '@/lib/query-client';
import { router } from './router';
// import { MoonPayProvider } from '@moonpay/moonpay-react';
import { AnimatePresence } from 'motion/react';
import { Toaster } from '@/components/ui/sonner';
import { ThemeProvider } from '@/components/theme-provider';
import { EventThemeProvider } from '@/providers/EventThemeProvider';
import { TooltipProvider } from '@/components/ui/tooltip';
import { CookiesProvider } from 'react-cookie';
import { AudioProvider } from '@/contexts/audio-context';
import { useConnectionMonitor } from '@/hooks/use-connection-monitor';
import { theme } from './constants/dapp-theme';
import { WALLET_INFO_STORAGE_KEY } from '@/constants/storage-keys';
import { WalletProvider as AuthWalletProvider } from '@/hooks/auth';
import { AuthProvider } from './providers/AuthProvider';
import { useActivityDetailsDialogStore } from '@/stores/activity-details-dialog-store';
import { useWalletConnectModalStore } from '@/stores/wallet-connect-modal-store';
import { useE2ETestWallet } from '@/hooks/useE2ETestWallet';
import { applyE2EDocumentHints } from '@/utils/e2e';
import { cancelIdleCallback, scheduleIdleCallback } from '@/utils/idle';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';

const ConnectionStatusListener = () => {
	useConnectionMonitor();
	return null;
};

const E2ERuntimeSetup = () => {
	useE2ETestWallet();

	useEffect(() => {
		applyE2EDocumentHints();
	}, []);

	return null;
};

const queryClient = createAppQueryClient();
const { networkConfig } = createNetworkConfig({
	devnet: { url: getFullnodeUrl('devnet') },
	testnet: { url: getFullnodeUrl('testnet') },
	mainnet: { url: 'https://wallet-rpc.mainnet.sui.io/' },
});

const THEME_STORAGE_KEY = 'suigar.preferences.theme';
const RegisterEnokiWalletsLazy = lazy(() => import('./utils/registerEnokiWallets'));
const GlobalConnectModalLazy = lazy(() => import('@/components/GlobalConnectModal').then((m) => ({ default: m.GlobalConnectModal })));
const GlobalActivityDetailsDialogLazy = lazy(() =>
	import('@/components/ActivityDetailsDialog').then((m) => ({ default: m.GlobalActivityDetailsDialog })),
);

export default function App() {
	const [isWalletBootstrapReady, setWalletBootstrapReady] = useState(false);
	const walletBootstrapRef = useRef(false);
	const isConnectModalOpen = useWalletConnectModalStore((state) => state.isOpen);
	const isActivityDialogOpen = useActivityDetailsDialogStore((state) => state.isOpen);

	useEffect(() => {
		const markReady = () => {
			if (walletBootstrapRef.current) return;
			walletBootstrapRef.current = true;
			setWalletBootstrapReady(true);
		};

		const handle = scheduleIdleCallback(markReady, 1500);
		if (typeof window !== 'undefined') {
			window.addEventListener('pointerdown', markReady, { once: true, passive: true });
			window.addEventListener('keydown', markReady, { once: true });
			window.addEventListener('touchstart', markReady, { once: true, passive: true });
		}

		return () => {
			cancelIdleCallback(handle);
			if (typeof window !== 'undefined') {
				window.removeEventListener('pointerdown', markReady);
				window.removeEventListener('keydown', markReady);
				window.removeEventListener('touchstart', markReady);
			}
		};
	}, []);

	useEffect(() => {
		const handle = scheduleIdleCallback(() => {
			void import('@/fonts');
		}, 800);

		return () => cancelIdleCallback(handle);
	}, []);

	useEffect(() => {
		if (!isWalletBootstrapReady) return;
		void import('@/lib/socket-listener');
	}, [isWalletBootstrapReady]);

	return (
		<HelmetProvider key={'helmet'}>
			<QueryClientProvider key={'queryClient'} client={queryClient}>
				<SuiClientProvider key={'suiClient'} defaultNetwork={APP_CFG.NETWORK} networks={networkConfig}>
					{isWalletBootstrapReady ? (
						<Suspense fallback={null}>
							<RegisterEnokiWalletsLazy />
						</Suspense>
					) : null}
					<WalletProvider key={'wallet'} slushWallet={{ name: 'Suigar' }} storageKey={WALLET_INFO_STORAGE_KEY} theme={theme}>
						<E2ERuntimeSetup />
						<CookiesProvider key={'cookies'}>
							<AuthWalletProvider key={'authWallet'} bootstrapReady={isWalletBootstrapReady}>
								{/* <MoonPayProvider apiKey={APP_CFG.MOONPAY_API_KEY} debug> */}
								<EventThemeProvider key={'eventTheme'}>
									<ThemeProvider key={'theme'} storageKey={THEME_STORAGE_KEY}>
										<TooltipProvider key={'tooltip'} delayDuration={0}>
											<AudioProvider key={'audio'}>
												<AuthProvider key={'auth'}>
													<AnimatePresence key={'animate'} mode="sync">
														<RouterProvider key="router" router={router} />
														{isConnectModalOpen ? (
															<Suspense key="globalConnectModal" fallback={null}>
																<GlobalConnectModalLazy />
															</Suspense>
														) : null}
														{isActivityDialogOpen ? (
															<Suspense key="globalActivityDetails" fallback={null}>
																<GlobalActivityDetailsDialogLazy />
															</Suspense>
														) : null}
														<ConnectionStatusListener key="connectionStatusListener" />
														<Toaster key="toaster" />
													</AnimatePresence>
												</AuthProvider>
											</AudioProvider>
										</TooltipProvider>
									</ThemeProvider>
								</EventThemeProvider>
								{/* </MoonPayProvider> */}
							</AuthWalletProvider>
						</CookiesProvider>
					</WalletProvider>
				</SuiClientProvider>
			</QueryClientProvider>
		</HelmetProvider>
	);
}
